/**
 * useGeolocation Hook
 *
 * Provides access to browser geolocation API. Automatically requests
 * user's current position and handles permissions/errors.
 *
 * @param options - Geolocation options (enableHighAccuracy, timeout, maximumAge)
 * @returns Object with coordinates, error, and loading state
 *
 * @example
 * ```tsx
 * const { coordinates, error, isLoading } = useGeolocation();
 *
 * if (isLoading) return <div>Getting location...</div>;
 * if (error) return <div>Error: {error}</div>;
 * if (coordinates) {
 *   return <div>Lat: {coordinates.latitude}, Lon: {coordinates.longitude}</div>;
 * }
 * ```
 */

import { useEffect, useState } from 'react';

type GeolocationCoordinates = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

type GeolocationState = {
  coordinates: GeolocationCoordinates | null;
  error: string | null;
  isLoading: boolean;
};

type GeolocationOptions = {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
};

export function useGeolocation(options?: GeolocationOptions): GeolocationState {
  const [state, setState] = useState<GeolocationState>({
    coordinates: null,
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    // Check if geolocation is available
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setState({
        coordinates: null,
        error: 'Geolocation is not supported by your browser',
        isLoading: false,
      });
      return;
    }

    // Request current position
    const successHandler = (position: GeolocationPosition) => {
      setState({
        coordinates: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        },
        error: null,
        isLoading: false,
      });
    };

    const errorHandler = (error: GeolocationPositionError) => {
      let errorMessage = 'Failed to get location';

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location permission denied';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information unavailable';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out';
          break;
      }

      setState({
        coordinates: null,
        error: errorMessage,
        isLoading: false,
      });
    };

    // Get current position with options
    navigator.geolocation.getCurrentPosition(
      successHandler,
      errorHandler,
      options || {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 60000, // Cache for 1 minute
      },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run once on mount only
  }, []);

  return state;
}
